import { useQuery } from '@tanstack/react-query';
import { billingApi } from '@/api/ops';
import type { FxRateList } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { MICRO_CREDITS_PER_REFERENCE_UNIT, moneyToMicroCredits } from './credits';
import { minorDigits } from './format';
import { LIVE } from './live';

/**
 * The FX rate billing uses for `currency` at `at`: units of `currency` per 1 reference unit — the newest row in force
 * then (`effective_from` ≤ `at`). The reference currency itself is 1. Null when billing has none (never a guess).
 */
export function fxRateAt(fx: FxRateList | null | undefined, currency: string, at: string | Date = new Date()): number | null {
  if (!fx) return null;
  const code = currency.toUpperCase();
  if (code === fx.reference_currency.toUpperCase()) return 1;
  const when = typeof at === 'string' ? Date.parse(at) : at.getTime();
  const rows = fx.fx_rates
    .filter((r) => r.currency.toUpperCase() === code && Date.parse(r.effective_from) <= when)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  return rows[0]?.units_per_reference ?? null;
}

/** Money (minor units) as micro-credits at billing's rate in force at `at` — null when no rate exists. */
export function moneyAsCredits(fx: FxRateList | null | undefined, minor: number, currency: string, at?: string): number | null {
  return moneyToMicroCredits(minor, minorDigits(currency), fxRateAt(fx, currency, at));
}

/** Micro-credits as money of the REFERENCE currency, in its minor units (100,000 µ = 1 reference unit, D-CP21). */
export function creditsAsReferenceMinor(micro: number, referenceCurrency: string): number {
  return Math.round((micro * 10 ** minorDigits(referenceCurrency)) / MICRO_CREDITS_PER_REFERENCE_UNIT);
}

/** Billing's FX table, live (billing:read). `null` data when the role cannot read it — the page then shows money only. */
export function useFxRates(env: string | null) {
  const allowed = hasPermission(PERMISSIONS.billingRead);
  const query = useQuery({
    queryKey: ['ops', 'billing', env, 'fx'],
    queryFn: () => billingApi.fxRates(env!),
    enabled: !!env && allowed,
    ...LIVE,
  });
  return { fx: query.data ?? null, allowed, failed: query.isError };
}
